import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginatedSubmissions } from '../../types/submission';

interface ResourceGridPaginationProps {
  page: PaginatedSubmissions | null;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/**
 * Pagination + page-size controls. Total pages are derived from the
 * response envelope when available; otherwise fall back to
 * `has_next_page` for cursor-style responses. Page-size selector
 * offers 20/50/100 — common values for the review workflow.
 */
export function ResourceGridPagination({
  page,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: ResourceGridPaginationProps) {
  const total = typeof page?.total === 'number' ? page.total : undefined;
  const totalPages = total !== undefined ? Math.max(1, Math.ceil(total / pageSize)) : undefined;
  const returnedCount = page?.data?.length ?? 0;

  const hasPrev = currentPage > 1;
  // Precedence for "is there a next page?":
  //   1. Explicit `has_next_page` when the server provides it.
  //   2. Derived from `total` when we know the total row count.
  //   3. Heuristic: if the current response filled `pageSize` rows,
  //      assume there's more. This is over-optimistic on exact-fit
  //      last pages (one wasted click), but never leaves the user
  //      stuck when the backend omits pagination hints — which was
  //      the actual failure mode we saw on staging.
  const hasNext =
    page?.has_next_page === true
      ? true
      : page?.has_next_page === false
        ? false
        : totalPages !== undefined
          ? currentPage < totalPages
          : returnedCount >= pageSize;

  const rangeStart = total === 0 || returnedCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    total !== undefined
      ? Math.min(currentPage * pageSize, total)
      : rangeStart + returnedCount - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rgf-gray-200 bg-white p-3 text-sm shadow-sm">
      <div className="flex items-center gap-2 text-rgf-gray-600">
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide">Rows</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border border-rgf-gray-300 bg-white px-2 py-1 text-sm focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
          >
            {[20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        {total !== undefined ? (
          <span className="text-xs text-rgf-gray-500">
            Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')}
          </span>
        ) : returnedCount > 0 ? (
          <span className="text-xs text-rgf-gray-500">
            Showing {rangeStart}–{rangeEnd}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrev}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rgf-gray-200 text-rgf-gray-600 hover:border-rgf-gray-300 hover:text-rgf-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[3rem] px-2 text-center text-xs text-rgf-gray-600">
          Page {currentPage}
          {totalPages !== undefined && ` of ${totalPages}`}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rgf-gray-200 text-rgf-gray-600 hover:border-rgf-gray-300 hover:text-rgf-gray-900 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
